import { useEffect, useRef, useState } from "react";
import type { VoiceTranscriptionProvider, VoiceTranscriptionSession } from "../../application/voice-transcription";
import { mergeVoiceTranscript } from "../../application/voice-transcript";

export function useListVoice(provider: VoiceTranscriptionProvider, onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const [starting, setStarting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");
  const session = useRef<VoiceTranscriptionSession | null>(null);
  const generation = useRef(0);
  const received = useRef("");
  const callback = useRef(onText);
  callback.current = onText;
  const cancel = () => { generation.current++; session.current?.abort(); session.current = null; setStarting(false); setListening(false); setProcessing(false); setError(""); setTranscript(""); };
  useEffect(() => () => { generation.current++; session.current?.abort(); }, []);
  const toggle = () => {
    if (session.current) { session.current.stop(); return; }
    setError(""); setTranscript(""); received.current = "";
    if (!provider.isSupported()) { setError("Голосовой ввод недоступен в этом браузере. Можно открыть «Ввести команду текстом» в списке."); return; }
    const token = ++generation.current;
    setStarting(true);
    session.current = provider.start("ru-RU", {
      onListening: () => { if (token === generation.current) { setStarting(false); setListening(true); } },
      onCaptureEnd: () => { if (token === generation.current) { setStarting(false); setListening(false); setProcessing(true); } },
      onInterim: text => { if (token === generation.current) setTranscript(mergeVoiceTranscript(received.current, text)); },
      onFinal: text => { if (token === generation.current) { received.current = mergeVoiceTranscript(received.current, text); setTranscript(received.current); } },
      onError: reason => {
        if (token !== generation.current) return;
        cancel();
        setError(reason === "permission" ? "Разрешите доступ к микрофону в настройках браузера." : reason === "network" ? "Нет связи со службой распознавания. Список доступен для ручного изменения." : "Не удалось распознать речь. Попробуйте ещё раз.");
      },
      onEnd: () => {
        if (token !== generation.current) return;
        session.current = null; setStarting(false); setListening(false); setProcessing(false);
        if (received.current.trim()) callback.current(received.current.trim());
      },
    });
  };
  return { starting, listening, processing, transcript, error, toggle, cancel, clearError: () => setError("") };
}
